import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, from } from 'rxjs';
import { Auth0Service } from '../auth/services/auth0.service';

/**
 * Handles 401 (Unauthorized) responses by refreshing the token and retrying the request
 * Follows Auth0 best practices:
 * - Only refreshes token when server returns 401
 * - Uses refresh_token grant type automatically
 * - Retries failed request with new token
 * - Logs out user if refresh fails
 *
 * Reference: https://auth0.com/docs/api/authentication#refresh-token
 */
export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth0Service = inject(Auth0Service);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle 401 Unauthorized errors for authenticated users
      if (error.status === 401 && auth0Service.isAuthenticated()) {
        // Token might be expired, try to refresh it
        return from(auth0Service.refreshAccessToken()).pipe(
          switchMap((newToken) => {
            // Retry the request with the new token
            const clonedReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
            });

            return next(clonedReq);
          }),
          catchError((refreshError) => {
            console.error('Token refresh failed:', refreshError);
            auth0Service.logout();

            return throwError(() => refreshError);
          })
        );
      }

      return throwError(() => error);
    })
  );
};
